import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { addInput, deleteInput, updateInput } from '../../../api/tabsApi';
import useForm, { TField } from '../../form/Form';
import { TState, useActions } from '../../../redux/store';
import { useCanI } from '../../../redux/actions';
import Modal from '../../modal/Modal';

export const addressInputs = [
  'rue',
  'code postal',
  'ville',
  'departement',
  'entière',
];
export const addressInputskeysImport = [
  'street',
  'postal',
  'city',
  'dept',
  'all',
];
export const addressInputskeys = ['street', 'postal', 'city', 'dept'];
export const addressInputsRoles = [
  'street',
  'postal',
  'city',
  'dept',
  'showValidation',
];

const typesOfInput = [
  { label: 'Text', value: 'text' },
  { label: 'Nombre', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Liste', value: 'select' },
  { label: 'Liste multiple', value: 'multiselect' },
  { label: 'Adresse', value: 'address' },
  { label: 'Téléphone', value: 'tel' },
  { label: 'Siret', value: 'siret' },
  { label: 'Utilisateur', value: 'users' },
  { label: 'Oui/Non', value: 'switch' },
  { label: 'Compteur', value: 'counter' },
  { label: 'Groupe répétable', value: 'repeatgroup' },
];

export const typesOfInputOptions = [
  { label: 'Text', value: 'text' },
  { label: 'Nombre', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Liste', value: 'select' },
  { label: 'Liste multiple', value: 'multiselect' },
  { label: 'Adresse entière', value: 'adresse_all' },
  { label: 'Adresse rue', value: 'adresse_street' },
  { label: 'Adresse ville', value: 'adresse_city' },
  { label: 'Adresse code postal', value: 'adresse_postal' },
  { label: 'Téléphone', value: 'tel' },
  { label: 'Utilisateur', value: 'users' },
  { label: 'Oui/Non', value: 'switch' },
  { label: 'Fichiers', value: 'files' },
  { label: 'RDV', value: 'planning' },
];

const useGetInputFields = () => {
  const userList = useSelector((state: TState) => state.userList);
  const modalType = useSelector((state: TState) => state.modalType);
  const roleList = useSelector((state: TState) => state.roleList);
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const isAdmin = useSelector((state: TState) => state.isAdmin);

  return () =>
    [
      {
        type: 'select',
        name: 'type',
        label: 'Type de champ',
        disabledFn: (data) => {
          return (
            modalType === 'editInput' &&
            (data.fromCRM || (data.type && !/select/.test(data.type)))
          );
        },
        optionsFn: (data) =>
          modalType === 'editInput' && /select/.test(data.type)
            ? typesOfInput.filter((o) => /select/.test(o.value))
            : typesOfInput,
      },
      {
        type: 'text',
        label: 'Nom du champ',
        name: 'label',
        condition: (data) => data?.type,
      },
      {
        type: 'text',
        label: 'ID du champ (si Nom existe déjà)',
        name: 'name_id',
        condition: (data) =>
          (data?.type && modalType === 'addInput') || isAdmin,
      },
      {
        type: 'text',
        label: 'Description',
        name: 'description',
        condition: (data) => data?.type,
      },
      {
        type: 'checkbox',
        label: "Afficher l'heure",
        name: 'showTime',
        condition: (data) => data?.type === 'date',
      },
      {
        type: 'multiselect',
        label: 'Champs à répéter',
        name: 'repeatFields',
        options: dataInputs
          ?.filter((i) => i.type !== 'repeatgroup')
          ?.map((input) => ({
            label: `${input.label ? `${input.label} / ` : ''}${input.name_id}`,
            value: input.name_id,
          })) as any,
        condition: (data) => data?.type === 'repeatgroup',
      },
      {
        type: 'multiselect',
        label: 'Champs à afficher dans la vue principale',
        name: 'repeatFieldsTable',
        optionsFn: (data) =>
          dataInputs
            ?.filter((i) => {
              return data.repeatFields?.includes(i.name_id);
            })
            ?.map((input) => ({
              label: `${input.label ? `${input.label} / ` : ''}${input.name_id}`,
              value: input.name_id,
            })) as any,
        condition: (data) => data?.type === 'repeatgroup',
      },
      {
        type: 'number',
        label: 'Nombre de répétition dans la vue principale',
        name: 'nbRepeatFieldsTable',
        condition: (data) => data?.type === 'repeatgroup',
      },
      {
        type: 'number',
        label: 'Répétition par default',
        name: 'defaultRepeat',
        condition: (data) => data?.type === 'repeatgroup',
      },
      {
        type: 'select',
        options:
          dataInputs
            ?.filter((i) => i.type === 'address')
            ?.map((i) => ({ label: i.name_id, value: i.name_id })) || [],
        label: 'Champ adresse associé',
        name: 'addressLinked',
        condition: (data) => data?.type === 'siret',
      },
      {
        type: 'select',
        options:
          dataInputs
            ?.filter((i) => i.type === 'text')
            ?.map((i) => ({ label: i.name_id, value: i.name_id })) || [],
        label: 'Champ société associé',
        name: 'companyLinked',
        condition: (data) => data?.type === 'siret',
      },
      {
        type: 'text',
        label: 'Unité',
        name: 'unity',
        condition: (data) => data?.type === 'number',
      },
      {
        type: 'selectInput',
        label: 'Éditer la liste',
        name: 'options',
        condition: (data) => /select/.test(data?.type),
      },
      ...addressInputskeys.map(
        (input, idx) =>
          ({
            type: 'checkbox',
            label: addressInputs[idx],
            name: input,
            condition: (data) => data?.type && data?.type === 'address',
          }) as TField,
      ),
      {
        type: 'checkbox',
        label: 'Est valide',
        name: 'showValidation',
        condition: (data) => data?.type && data?.type === 'address',
      },
      {
        type: 'multiSelect',
        label: 'Utilisateurs',
        name: 'users',
        options: userList.map((user) => ({
          label: user.name,
          value: user._id,
          color: user.userColor,
        })) as any,
        condition: (data) => data?.type && data?.type === 'users',
      },
      {
        type: 'checkbox',
        label: 'Champ obligatoire',
        name: 'required',
        condition: (data) => data?.type && data?.type !== 'repeatgroup',
      },
      {
        type: 'checkbox',
        label: 'Champ conseilé',
        name: 'advised',
        condition: (data) => data?.type && data?.type !== 'repeatgroup',
      },
      {
        type: 'checkbox',
        label: 'Champ avec colonne daté',
        name: 'dated',
        condition: (data) => data?.type && data?.type !== 'repeatgroup',
      },
      {
        type: 'checkbox',
        label: 'Afficher la colonne daté au format - Il y a ...',
        name: 'isAgo',
        condition: (data) => data?.dated,
      },
      {
        type: 'multiselect',
        options: [
          { label: `admin ${localStorage.getItem('company')}`, value: '000' },
          ...roleList.map((r) => ({ label: r.name, value: r._id })),
        ],
        label: 'Afficher la colonne daté pour les roles',
        name: 'datedRoles',
        placeholder: 'Visible par tous les rôles',
        condition: (data) => data?.dated,
      },
      {
        type: 'createSelect',
        label: 'Groupe',
        name: 'group',
        condition: (data) => data?.type,
      },
    ] as TField[];
};

const ManageInputs = () => {
  const userList = useSelector((state: TState) => state.userList);
  const rowData = useSelector((state: TState) => state.rowData);
  const modalType = useSelector((state: TState) => state.modalType);
  const dataInputs = useSelector((state: TState) => state.dataInputs);

  const getInputFields = useGetInputFields();

  const { setRowData, onClose, getInputs, setModalType } = useActions();

  const canI = useCanI();
  const toast = useToast();

  const fields = useMemo(
    () => getInputFields(),
    [modalType, userList, dataInputs],
  );
  const { Form, formData, setErrors } = useForm(fields);

  useEffect(() => {
    if (rowData.type === formData.type || modalType === 'editInput') return;

    const resetData: any = {
      _id: formData._id,
      type: formData.type,
      name_id: formData.name_id,
      label: formData.label,
      description: formData.description,
      group: formData.group,
      required: formData.required,
      dated: formData.dated,
    };

    if (/select/.test(modalType)) {
      resetData.options = formData.options;
      resetData.orderedOptions = formData.orderedOptions;
    }

    !/del/.test(modalType) && setRowData(resetData);
  }, [formData.type]);

  useEffect(() => {
    setRowData((fd: any) => ({
      ...fd,
      ...formData,
      repeatFieldsTable: formData.repeatFieldsTable?.filter((e: any) =>
        formData.repeatFields?.includes(e),
      ),
    }));
  }, [formData.repeatFields]);

  const handleAddInput = async () => {
    setTimeout(async () => {
      if (!formData.label) {
        toast({
          title: 'Vous devez donner un nom au champ',
          status: 'warning',
        });
        return;
      }

      if (/select/.test(formData.type) && !formData.options?.length) {
        toast({
          title: 'Vous devez ajouter au moins une option',
          status: 'warning',
        });
        return;
      }

      setErrors({});
      const resp = await addInput(formData);

      if (resp) {
        await getInputs({ forced: true });

        setModalType('editInput');
        setRowData(resp.data);
      } else {
        setErrors((e) => ({ ...e, ['name_id']: { message: 'duplicate' } }));
      }
    }, 100);
  };

  const handleEditInput = async () => {
    setTimeout(async () => {
      const resp = await updateInput(formData);

      if (resp.existing) {
        setRowData({ ...formData, name_id: resp.data.name_id });
        toast({
          title: "Impossible de modifier l'ID car il est déjà utilisé",
          status: 'warning',
        });
      }

      await getInputs({ forced: true });
    }, 100);
  };

  const handleDeleteInput = async () => {
    await deleteInput(rowData._id);
    await getInputs({ forced: true });
    onClose();
  };

  const renderManageInputs = (
    <>
      {Form.map((f: any) => {
        return f.props.field.type === 'checkbox' ? (
          <Flex w='100%' gap={3} alignItems='center'>
            <Box>{f}</Box>
          </Flex>
        ) : (
          f
        );
      })}
    </>
  );

  const deletePermission = canI('inputs', 'delete');

  const renderDeleteInput = <Text>Confirmer la suppression ?</Text>;

  const modalOptions = {
    addInput: {
      component: renderManageInputs,
      action: handleAddInput,
      title: 'Ajouter champ',
      actionLabel: 'Ajouter',
    },
    editInput: {
      component: renderManageInputs,
      canDelete: deletePermission ? 'deleteInput' : '',
      action: handleEditInput,
      title: 'Editer champ',
      actionLabel: 'Sauvegarder',
    },
    deleteInput: {
      component: renderDeleteInput,
      action: handleDeleteInput,
      title: 'Supprimer champ',
      actionLabel: 'Confirmer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageInputs;
